import React, { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff, KeyRound, Loader2, CheckCircle2, XCircle, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { getLlmSettings, loadLlmSettings, saveLlmSettings, clearLlmSettings, useOnOpenLlmSettings } from "@/lib/llmSettings";

const CUSTOM = "__custom";

function ModelPicker({ provider, model, setModel }) {
  const listed = provider?.models || [];
  const isCustom = model !== "" && !listed.includes(model);
  const [custom, setCustom] = useState(isCustom);
  useEffect(() => setCustom(model !== "" && !listed.includes(model)), [provider?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-1.5">
      <Label>Model</Label>
      <Select
        value={custom ? CUSTOM : model}
        onValueChange={(v) => { if (v === CUSTOM) { setCustom(true); setModel(""); } else { setCustom(false); setModel(v); } }}
      >
        <SelectTrigger data-testid="llm-model-select"><SelectValue placeholder="Pick a model…" /></SelectTrigger>
        <SelectContent>
          {listed.map((m, i) => (
            <SelectItem key={m} value={m} data-testid={`llm-model-option-${m}`}>
              {m}{i === 0 ? "  (recommended)" : ""}
            </SelectItem>
          ))}
          <SelectItem value={CUSTOM} data-testid="llm-model-option-custom">Custom model name…</SelectItem>
        </SelectContent>
      </Select>
      {custom && (
        <Input
          placeholder="e.g. claude-opus-4-7"
          value={model}
          onChange={(e) => setModel(e.target.value.trim())}
          data-testid="llm-custom-model-input"
        />
      )}
    </div>
  );
}

function KeyField({ provider, apiKey, setApiKey, hint }) {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label>API key</Label>
        {provider?.key_url && (
          <a href={provider.key_url} target="_blank" rel="noreferrer" className="text-xs text-cyan-400 hover:underline inline-flex items-center gap-1" data-testid="llm-get-key-link">
            Get a {provider.label.split(" ")[0]} key <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
      <div className="relative">
        <Input
          type={show ? "text" : "password"}
          autoComplete="off"
          placeholder={hint ? `Saved key ${hint} — paste a new one to replace it` : "Paste your API key"}
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value.trim())}
          className="pr-9 font-mono text-xs"
          data-testid="llm-api-key-input"
        />
        <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="llm-api-key-toggle">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function TestResult({ test }) {
  if (!test) return null;
  if (test.loading) return <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Testing connection…</p>;
  return test.ok ? (
    <p className="text-xs text-emerald-400 flex items-center gap-1.5" data-testid="llm-test-success"><CheckCircle2 className="w-3.5 h-3.5" /> Connected — {test.model} replied.</p>
  ) : (
    <p className="text-xs text-rose-300 flex items-start gap-1.5 break-words" data-testid="llm-test-error"><XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {test.error}</p>
  );
}

export function LlmSettingsDialog() {
  const [open, setOpen] = useState(false);
  const [providers, setProviders] = useState([]);
  const [providerId, setProviderId] = useState("anthropic");
  const [model, setModel] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [test, setTest] = useState(null);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);

  const onOpen = useCallback(async () => {
    const s = getLlmSettings() || (await loadLlmSettings());
    setSaved(s);
    setProviderId(s?.provider || "anthropic");
    setModel(s?.model || "");
    setApiKey("");
    setTest(null);
    setError(null);
    setOpen(true);
  }, []);
  useOnOpenLlmSettings(onOpen);

  useEffect(() => {
    if (open && !providers.length) api.llmProviders().then(setProviders).catch((e) => console.warn("Could not load LLM providers", e));
  }, [open, providers.length]);

  const provider = providers.find((p) => p.id === providerId);
  useEffect(() => {
    if (provider && !model) setModel(provider.models[0]);
  }, [provider, model]);

  const usesSavedKey = !apiKey && saved?.has_key && saved.provider === providerId;
  const valid = Boolean(providerId && model && (apiKey || usesSavedKey));

  const runTest = async () => {
    setTest({ loading: true });
    try {
      const r = await api.llmTest(apiKey ? { provider: providerId, model, apiKey } : null);
      setTest({ ok: true, model: r.model });
    } catch (e) {
      setTest({ ok: false, error: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg" data-testid="llm-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="w-4 h-4 text-cyan-400" /> LLM settings</DialogTitle>
          <DialogDescription>
            Use your own API key and model. The key is saved encrypted on your account (so it works on any device) and is never shown back in the browser. Usage is billed to your provider account.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Provider</Label>
            <Select value={providerId} onValueChange={(v) => { setProviderId(v); setModel(""); setTest(null); }}>
              <SelectTrigger data-testid="llm-provider-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {providers.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`llm-provider-option-${p.id}`}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <ModelPicker provider={provider} model={model} setModel={(m) => { setModel(m); setTest(null); }} />
          <KeyField provider={provider} apiKey={apiKey} hint={saved?.provider === providerId ? saved?.key_hint : ""} setApiKey={(k) => { setApiKey(k); setTest(null); }} />
          <TestResult test={test} />
          {error && <p className="text-xs text-rose-300" data-testid="llm-save-error">{error}</p>}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" className="text-rose-300" onClick={() => clearLlmSettings().then((s) => { setSaved(s); setApiKey(""); setTest(null); }, (e) => setError(e?.response?.data?.detail || e.message))} data-testid="llm-remove-key-btn">
            Remove key
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" disabled={!valid || test?.loading} onClick={runTest} data-testid="llm-test-btn">Test connection</Button>
            <Button disabled={!valid} onClick={() => saveLlmSettings({ provider: providerId, model, api_key: apiKey || undefined }).then(() => setOpen(false), (e) => setError(e?.response?.data?.detail || e.message))} data-testid="llm-save-btn">Save</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
